/**
 * 管理端补消 API（ADM-33，makeup-deduction）。
 *
 * 补消 = 对过去已上课课次按学生补做消课扣减（整课漏消 / 个别漏消）。
 * 合格课次与名单为只读查询；提交必填中文原因，409 业务拒绝经 Transport 抛
 * ApiError（后端文案已含学生名），由弹窗直接提示。
 */
import { ApiPaths } from './api.config';
import type { Paginated } from './campuses.api';
import type {
  DeductionBatchListItem,
  DeductionBatchListQuery,
} from './deduction.api';
import { http } from './transport';

/** 补消合格课次列表行（后端 MakeupEligibleSessionDto）。 */
export interface MakeupEligibleSession {
  id: string;
  classId: string;
  className: string;
  skuName: string;
  teacherName: string;
  classroomName: string;
  /** 业务日期 YYYY-MM-DD。 */
  sessionDate: string;
  startAt: string;
  endAt: string;
  /** SCHEDULED（整课漏消）或 COMPLETED（个别漏消）。 */
  status: string;
  /** 课次时点在班人数（名单口径分母）。 */
  studentCount: number;
  /** 本课次已生效消课人数（分子）。 */
  deductedCount: number;
}

/** 本课次该学生的最新消课状态（弹窗第二步勾选依据）。 */
export type MakeupDeductionState = 'DEDUCTED' | 'REVERSED' | 'SKIPPED' | 'NONE';

/** 跨 SKU 账户摘要（本课程查无账户时透出课时去向；后端 CrossSkuAccountSummary）。 */
export interface CrossSkuAccountSummary {
  skuName: string;
  formalAvailable: number;
  giftedAvailable: number;
}

/** 名单预演账户状态（账户 = 学生 × SKU 子账户；区分无账户、已失效与课时不足）。 */
export type RosterAccountState = 'OK' | 'NO_ACCOUNT' | 'EXPIRED_VOID' | 'BALANCE_EXHAUSTED';

/** 补消名单学生行（后端 MakeupRosterStudentDto）。 */
export interface MakeupRosterStudent {
  periodCoverage: "NONE" | "COVERED" | "WILL_ACTIVATE";
  periodPassId: string | null;
  periodSummary: string | null;
  studentId: string;
  name: string;
  studentNo: string;
  /** 本笔将扣哪个池（正式优先；无可扣课时 null，据 accountState 区分原因）。 */
  deductLessonKind: 'FORMAL' | 'GIFTED' | null;
  balance: number | null;
  /** 余额偏低警示（将扣池余额 ≤ 1）。 */
  balanceWarning: boolean;
  /** 账户状态（区分无账户、已失效与课时不足）。 */
  accountState: RosterAccountState;
  deductionState: MakeupDeductionState;
  /** 上次被跳过的原因（仅 SKIPPED 非空）。 */
  lastFailReason: string | null;
  /** 是否可勾选补消（已 DEDUCTED 禁勾）。 */
  selectable: boolean;
  /** 其它课程的课时去向（本课程有账户时为空数组）。 */
  crossSkuAccounts: CrossSkuAccountSummary[];
}

/** 补消名单评估结果（课次概要 + 时点在班学生逐行状态）。 */
export interface MakeupRoster {
  sessionId: string;
  classId: string;
  className: string;
  skuName: string;
  classroomName: string;
  sessionDate: string;
  startAt: string;
  endAt: string;
  studentCount: number;
  students: MakeupRosterStudent[];
}

/** 补消提交逐生结果（后端 DeductionBatchItemDto）。 */
export interface MakeupSubmitItem {
  studentId: string;
  /** 本笔扣减所属池（失败行 null）。 */
  deductLessonKind: 'FORMAL' | 'GIFTED' | null;
  result: 'DEDUCTED' | 'PERIOD_COVERED' | 'SKIPPED_NO_BALANCE' | 'SKIPPED_ACCOUNT_INVALID';
  quantity: number;
  ledgerEntryId: string | null;
  failReason: string | null;
  idempotentHit?: boolean;
}

/** 补消提交结果（后端 SubmitMakeupDeductionResultDto）。 */
export interface MakeupSubmitResult {
  batchId: string;
  sessionId: string;
  expectedCount: number;
  successCount: number;
  failedCount: number;
  items: MakeupSubmitItem[];
}

/** 合格课次查询参数（班级/校区/业务日期区间 + 分页）。 */
export interface MakeupEligibleQuery {
  classId?: string;
  campusId?: string;
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  pageSize?: number;
}

/** 补消记录查询参数（与消课批次同构，类型由端点固定 MAKEUP）。 */
export type MakeupRecordQuery = Omit<DeductionBatchListQuery, 'batchType'>;

function buildQuery(query: object): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== null && `${value}` !== '') {
      params.set(key, `${value}`);
    }
  }
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

async function listEligibleSessions(
  query: MakeupEligibleQuery = {},
): Promise<Paginated<MakeupEligibleSession>> {
  return http.get<Paginated<MakeupEligibleSession>>(
    `${ApiPaths.makeupDeductions.eligibleSessions}${buildQuery(query)}`,
  );
}

async function getRoster(sessionId: string): Promise<MakeupRoster> {
  return http.get<MakeupRoster>(
    `${ApiPaths.makeupDeductions.roster}${buildQuery({ sessionId })}`,
  );
}

async function submitMakeupDeduction(
  sessionId: string,
  studentIds: string[],
  reason: string,
): Promise<MakeupSubmitResult> {
  return http.post<MakeupSubmitResult>(ApiPaths.makeupDeductions.submit, {
    sessionId,
    studentIds,
    reason,
  });
}

async function listRecords(
  query: MakeupRecordQuery = {},
): Promise<Paginated<DeductionBatchListItem>> {
  return http.get<Paginated<DeductionBatchListItem>>(
    `${ApiPaths.makeupDeductions.list}${buildQuery(query)}`,
  );
}

/** 补消 API 唯一对外入口。 */
export const makeupDeductionApi = {
  listEligibleSessions,
  getRoster,
  submitMakeupDeduction,
  listRecords,
};
